import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import weekday from 'dayjs/plugin/weekday';
import { SelectOption } from 'core/utilities/interface-helpers';

dayjs.locale('en-gb');
dayjs.extend(weekday);

/**
 * The date range the report is asked for.
 *
 * Presets rather than two date pickers by default: the questions people
 * actually ask are about this month, last month, or the quarter, and making
 * them assemble that from two calendars every time is friction for no gain.
 * Custom is there for the one time in ten it is a genuinely arbitrary range.
 */

export interface IReportRange {
  startDate: string;
  endDate: string;
}

export enum RangePreset {
  THIS_WEEK = 'this-week',
  THIS_MONTH = 'this-month',
  LAST_MONTH = 'last-month',
  LAST_THREE_MONTHS = 'last-three-months',
  THIS_YEAR = 'this-year',
  CUSTOM = 'custom',
}

export const rangePresetOptions: SelectOption[] = [
  { value: RangePreset.THIS_WEEK, label: 'This week' },
  { value: RangePreset.THIS_MONTH, label: 'This month' },
  { value: RangePreset.LAST_MONTH, label: 'Last month' },
  { value: RangePreset.LAST_THREE_MONTHS, label: 'Last 3 months' },
  { value: RangePreset.THIS_YEAR, label: 'This year' },
  { value: RangePreset.CUSTOM, label: 'Custom range' },
];

const FORMAT = 'YYYY-MM-DD';

/**
 * Ranges end today rather than at the end of the period.
 *
 * A month-to-date total sitting next to a full previous month is the honest
 * comparison; running the end date to the 31st would show a month that looks
 * like it collapsed halfway through.
 */
export const rangeFor = (preset: RangePreset): IReportRange => {
  const today = dayjs();

  switch (preset) {
    case RangePreset.THIS_WEEK:
      return { startDate: today.weekday(0).format(FORMAT), endDate: today.format(FORMAT) };
    case RangePreset.LAST_MONTH: {
      const lastMonth = today.subtract(1, 'month');
      return {
        startDate: lastMonth.startOf('month').format(FORMAT),
        endDate: lastMonth.endOf('month').format(FORMAT),
      };
    }
    case RangePreset.LAST_THREE_MONTHS:
      return {
        startDate: today.subtract(2, 'month').startOf('month').format(FORMAT),
        endDate: today.format(FORMAT),
      };
    case RangePreset.THIS_YEAR:
      return { startDate: today.startOf('year').format(FORMAT), endDate: today.format(FORMAT) };
    case RangePreset.THIS_MONTH:
    default:
      return { startDate: today.startOf('month').format(FORMAT), endDate: today.format(FORMAT) };
  }
};

/** How a range reads in a sentence, e.g. "1 Sep to 8 Sep 2026". */
export const describeRange = ({ startDate, endDate }: IReportRange): string => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const sameYear = start.year() === end.year();

  return `${start.format(sameYear ? 'D MMM' : 'D MMM YYYY')} to ${end.format('D MMM YYYY')}`;
};

/** Used in a filename, so it sorts and needs no explanation. */
export const rangeForFilename = ({ startDate, endDate }: IReportRange): string => `${startDate}_to_${endDate}`;

export const isRangeValid = ({ startDate, endDate }: IReportRange): boolean =>
  !!startDate && !!endDate && !dayjs(endDate).isBefore(dayjs(startDate), 'day');
